/**
 * Analytics dispatch bus.
 *
 * `custom_event(action, label, value, category)` is the single entry point that
 * fans out one tracking call to every active pipeline. Today that is:
 *   - GTM    (unchanged — same dataLayer shape vue-gtm produces)
 *   - Rybbit (normalized via rybbit_event_from_custom)
 *
 * Existing inline Facebook Pixel / Clarity / LogRocket calls are intentionally
 * left at their call sites for now and are NOT routed through this bus.
 *
 * Phasing out GTM later = delete the `gtm_track_event(...)` line below.
 *
 * Usage (replaces `this.$gtm.trackEvent({event:'customEvent', category, action, label, value})`):
 *   import { custom_event } from '<relative path>/integrations'
 *   custom_event('initiateCheckout', email, price, 'userBehaviour')
 */

import { rybbit_event_from_custom } from './rybbit'

/**
 * Push a GTM custom event onto the dataLayer. Replicates exactly what the
 * vue-gtm plugin's `trackEvent({event:'customEvent', ...})` emits, but is
 * callable from plain modules (no Vue instance / `this.$gtm` needed).
 *
 * When `action` is an object it is treated as a complete event payload
 * (e.g. `{event:'user_data', user:{...}}`) and pushed as-is, mirroring
 * tazio-fe's gtm_track_event.
 */
export function gtm_track_event(action, label, value, category) {
  const dataLayer = (window.dataLayer = window.dataLayer || [])

  if (typeof action === 'object') {
    dataLayer.push(action)
    return
  }

  dataLayer.push({
    event: 'customEvent',
    category: category,
    action: action,
    label: label,
    value: value,
  })
}

/**
 * Fan out a single analytics event to all active pipelines.
 *
 * @param {string|Object} action - GA4-style event/action name (e.g. 'initiateCheckout') OR a complete event payload object
 * @param {string} [label]
 * @param {number} [value]
 * @param {string} [category] - e.g. 'userBehaviour', 'partnerBehaviour'
 */
export function custom_event(action, label, value, category) {
  // GTM — unchanged behaviour
  gtm_track_event(action, label, value, category)
  // Rybbit — normalized custom_event → Rybbit event
  rybbit_event_from_custom(action, label, value, category)
}

// Re-export the Rybbit public API for convenient single-import access.
export {
  RYBBIT_CONFIG,
  PROPERTIES_LIMIT,
  SUSPICIOUS_KEYS,
  rybbit_is_available,
  rybbit_event,
  rybbit_track_event,
  rybbit_event_from_custom,
  track_form_event,
  format_validation_errors,
  format_service_error,
  RybbitFormMixin,
} from './rybbit'
